import React, { useState } from 'react';
/**
 * `SaveQuoteConfirmation` confirms key personal details before saving a quote.
 *
 * Responsibilities:
 * - Captures title, surname, and email (+ confirm email) for the quote header.
 * - Validates required fields, email format, and length constraints.
 * - Captures marketing preference ("do not contact") for the quote.
 * - Builds a `QuoteRequest` via `getQuotesData` and saves via `QuotesApi`.
 * - On success, shows a toast and updates journey state (found quotes, quote id,
 *   quoted products) before closing the modal.
 */
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { ButtonCommand } from './Button/ButtonCommand';
import { ButtonConfirm } from './Button/ButtonConfirm';
import { ButtonIcon } from './Button/ButtonIcon';
import TitleSelectInput from '../common/PersonalDetail/TitleSelectInput';
import { QuotesApi } from '../../api/QuotesApi';
import {
  PersonalDetailType,
  ProductType,
  QuoteApiResponse,
  QuoteRequest,
  VehicleState
} from './CommonTypes';
import { getQuotesData } from './HelperMethods';
import MarketingPreferences, {
  MarketingPreferenceOptions
} from './MarketingPreferences';
import { useJourney } from '../sitePages/Layout';
import { CommonValidation } from './CommonValidation/commonValidation';
import TextInput from './TextInput/TextInput';
import { useTranslation } from 'react-i18next';
import { ProductChannel } from '../../consts/Common';
import { useAppSelector } from '../../app-state/Hooks';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

export type SaveQuoteConfirmationProps = {
  showModal: boolean;
  onCloseModal: () => void;
  closeModalOnConfirm: () => void;
  products: ProductType[];
  vehicle: VehicleState;
  quoteId: number;
  personalDetails: PersonalDetailType;
  setPersonalDetails: (personalDetail: PersonalDetailType) => void;
  quoteType: number;
  channels: number;
};

interface ErrorObject {
  title: string;
  surname: string;
  email: string;
  confirmEmail: string;
}

const SaveQuoteConfirmation = ({
  showModal,
  onCloseModal,
  closeModalOnConfirm,
  products,
  vehicle,
  quoteId,
  personalDetails,
  setPersonalDetails,
  quoteType,
  channels
}: SaveQuoteConfirmationProps): JSX.Element => {
  const [emailEnter, setEmailEnter] = useState(personalDetails.email);
  const [emailConfirm, setEmailConfirm] = useState(personalDetails.email);
  const [title, setTitle] = useState(personalDetails.title);
  const [surname, setSurname] = useState(personalDetails.surname);
  const [confirmDisabled, setConfirmDisabled] = useState(false);
  const [doNotContact, setDoNotContact] = useState(false);

  const [validationErrors, setValidationErrors] = useState<ErrorObject>({
    title: '',
    surname: '',
    email: '',
    confirmEmail: ''
  });

  const user = useAppSelector((s) => s.user);
  const brand = useAppSelector((s) => s.brand);
  const countryCode = useAppSelector((s) => s.appConfig.countryCode);
  const content = useAppSelector((s) => s.content);
  const {
    onUpdateFoundExistingQuotes,
    setQuoteId,
    onUpdateQuotedProducts,
    journey
  } = useJourney();

  const { quoteProducts } = journey;

  const { t } = useTranslation();

  const cms = content.productVariantPage;

  const marketingOptions: MarketingPreferenceOptions = {
    doNotContact: doNotContact
  };

  const onCancel = () => {
    onCloseModal();
  };

  const onConfirm = () => {
    if (validate()) {
      setConfirmDisabled(true);
      saveQuote();

      const updatedPersonalDetails = {
        ...personalDetails,
        title: title,
        surname: surname,
        email: emailEnter,
        emailConfirm: emailConfirm
      };

      setPersonalDetails(updatedPersonalDetails);
    } else {
      return false;
    }
  };

  const saveQuote = () => {
    const controller = new AbortController();
    const quotesData = getQuotesData(
      products,
      vehicle,
      user,
      quoteId,
      emailConfirm,
      title,
      surname,
      marketingOptions,
      quoteProducts,
      quoteType,
      channels.toString(),
      channels === ProductChannel.OpenSite
        ? brand.openSiteDealerCode
        : undefined
    );

    const quoteRequest: QuoteRequest = {
      quote: quotesData,
      countryCode: countryCode
    };

    const successCallback = (data: QuoteApiResponse) => {
      if (data.isSaved) {
        toast.success(
          t(
            'customer-product-page.save-quote-confirmation.quote-toast-successmsg'
          ),
          {
            onClose: () => {
              onUpdateFoundExistingQuotes(true);
              if (data.quoteDto?.quoteHeader.id) {
                setQuoteId && setQuoteId(data.quoteDto?.quoteHeader.id);
              }

              data.quoteDto?.quoteProducts &&
                onUpdateQuotedProducts(data.quoteDto?.quoteProducts);
              closeModalOnConfirm();
            },
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored'
          }
        );
      }

      setValidationErrors({
        title: '',
        surname: '',
        email: '',
        confirmEmail: ''
      });
      setConfirmDisabled(false);
    };

    const failureCallback = () => {
      console.log('Error occured in quote api save...');
      setConfirmDisabled(false);
    };

    new QuotesApi().saveQuote(
      quoteRequest,
      user.token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onChangeEmailEnter = (e: React.FormEvent<HTMLInputElement>) => {
    setEmailEnter(e.currentTarget.value);
    setValidationErrors({
      ...validationErrors,
      email: ''
    });
  };

  const onChangeSurname = (e: React.FormEvent<HTMLInputElement>) => {
    setSurname(e.currentTarget.value);
    setValidationErrors({
      ...validationErrors,
      surname: ''
    });
  };

  const onChangeEmailConfirm = (e: React.FormEvent<HTMLInputElement>) => {
    setEmailConfirm(e.currentTarget.value);
    setValidationErrors({
      ...validationErrors,
      confirmEmail: ''
    });
  };

  const validate = (): boolean => {
    const errors: ErrorObject = {
      title: '',
      surname: '',
      email: '',
      confirmEmail: ''
    };

    const emailCharLimit = 50;

    if (CommonValidation.isEmpty(title)) {
      errors.title = t(
        'customer-product-page.save-quote-confirmation.invalid-title-errormsg'
      );
    }

    if (CommonValidation.isEmpty(surname)) {
      errors.surname = t('personal-details.surname-required-validation');
    }

    if (CommonValidation.isEmpty(emailEnter)) {
      errors.email = 'Email address is required';
    }

    if (!CommonValidation.isValidEmail(emailEnter)) {
      errors.email = t(
        'customer-product-page.save-quote-confirmation.invalid-email-errormsg'
      );
    }

    if (!CommonValidation.isValidLength(emailEnter, emailCharLimit)) {
      errors.email = t(
        'customer-product-page.save-quote-confirmation.email-exceeds-length-errormsg',
        {
          emailCharLimit: emailCharLimit
        }
      );
    }

    if (CommonValidation.isEmpty(emailConfirm)) {
      errors.confirmEmail = t(
        'customer-product-page.save-quote-confirmation.confirm-email-errormsg'
      );
    }

    if (emailEnter !== emailConfirm) {
      errors.confirmEmail = t(
        'customer-product-page.save-quote-confirmation.confirm-email-match-errormsg'
      );
    }

    if (errors.title || errors.surname || errors.email || errors.confirmEmail) {
      setValidationErrors(errors);
      return false;
    } else {
      return true;
    }
  };

  const onChangeTitle = (value: string) => {
    if (value === '(null)') {
      setTitle('');
    } else {
      setTitle(value);
      setValidationErrors({
        ...validationErrors,
        title: ''
      });
    }
  };

  return (
    <Modal isOpen={showModal}>
      <ModalHeader
        toggle={onCloseModal}
        close={
          <ButtonIcon
            icon={faXmark}
            size='xs'
            className='close'
            onClick={onCancel}
            ariaLabel={t('button-text-close')}
          />
        }
        className='modal-header-title cms-modal-header-title justify-content-between'>
        <span>
          {t('customer-product-page.save-quote-confirmation.header-text')}
        </span>
      </ModalHeader>
      <ModalBody className='modal-body'>
        <div className='inline-control'>
          <label className='control-label' htmlFor='title'>
            {t('personal-details.title-label')}
          </label>
          <div className='control'>
            <TitleSelectInput
              value={title}
              token={user.token}
              error={validationErrors.title}
              onSelectedValueChange={onChangeTitle}
              countryCode={countryCode}
            />
          </div>
          <label className='control-label' htmlFor='surname'>
            {t('personal-details.surname-label')}
          </label>
          <div className='control'>
            <TextInput
              placeholder={t('personal-details.surname-label')}
              name='surname'
              className='input-text w250'
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onChangeSurname(e)
              }
              value={surname}
              error={validationErrors.surname}
            />
          </div>
          <label className='control-label' htmlFor='emailEnter'>
            {t(
              'customer-product-page.save-quote-confirmation.enter-email-address-label'
            )}
          </label>
          <div className='control'>
            <TextInput
              placeholder={t('personal-details.email-address-label')}
              name='emailEnter'
              className='input-text w250'
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onChangeEmailEnter(e)
              }
              value={emailEnter}
              error={validationErrors.email}
            />
          </div>
          <label className='control-label' htmlFor='emailConfirm'>
            {t('personal-details.confirm-email-address-label')}
          </label>
          <div className='control'>
            <TextInput
              placeholder={t('personal-details.confirm-email-address-label')}
              name='emailConfirm'
              className='input-text w250'
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onChangeEmailConfirm(e)
              }
              value={emailConfirm}
              error={validationErrors.confirmEmail}
            />
          </div>
          <div className='control'>
            <MarketingPreferences
              options={marketingOptions}
              setDoNotContact={setDoNotContact}
              disclaimer={cms.disclaimer}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className='flex-row button-row'>
          <ButtonCommand
            name='btn-close-confirmation'
            text={t('customer-button.button-cancel-text')}
            onClick={onCancel}
          />
          <ButtonConfirm
            name='btn-confirm-manual-vehicle'
            text={t('customer-button.button-confirm-text')}
            additionalClassNames='m05-left'
            onClick={onConfirm}
            disabled={confirmDisabled}
          />
          <ToastContainer />
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default SaveQuoteConfirmation;
